import React from 'react';
import { Box, Typography } from '@mui/material';
import { AppBarStyles } from '@client/style/style.constants';
import VerticalBar, {
  type VerticalBarSegment,
} from '@features/proposals/ProposalsOverview/VerticalBar';
import type { ISimpleBarChartValue } from '@ui/BarChart/SimpleBarChart';

export type OverviewBarShellProps = {
  /** Width (in px) of the CO2 bar. */
  barWidth: number;
  /** Name band width can exceed a narrow GFA bar without changing its area scale. */
  nameWidth?: number;
  /** Height of the band containing the name and optional secondary line. */
  nameAreaHeight?: number;
  showNameBottomBorder?: boolean;
  showCo2Label?: boolean;
  /** Maximum height (in px) of the CO2 bar. */
  maxBarHeight: number;
  maxBarHeightCss?: string;
  /** Bar fill color. */
  color: string;
  co2eChartData: ISimpleBarChartValue[];
  segments?: VerticalBarSegment[];
  co2ePerGFA: number;
  co2ePerGfaMax: number;
  backdropCo2ePerGFA?: number;
  backdropColor?: string;
  backdropCostBarHeight?: number;
  costBarHeight?: number;
  costLabel?: string;
  showInnerBorders?: boolean;
  innerBorderColor?: string;
  outlineOnly?: boolean;
  showCo2LabelIcon?: boolean;
  tooltipPlacement?: 'left' | 'right';
  diffValue?: number | null;
  /**
   * When true, render the name area with the tab-style background, side
   * notches and rounded corners. Use this for the currently selected /
   * active bar.
   */
  active?: boolean;
  /**
   * Click handler for the entire bar (CO2 bar + name area).
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Optional context-menu handler (used by v1 ProposalBar's kebab).
   */
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /**
   * Cursor style for the root element. Defaults to `'pointer'`.
   */
  cursor?: React.CSSProperties['cursor'];
  /**
   * Either a string label rendered as a centered name, or arbitrary
   * children (used by v1 ProposalBar to render its inline-edit field
   * and kebab menu).
   */
  name: string | React.ReactNode;
  /**
   * Accessible name of the whole bar (chart + name). The name input is
   * readonly with pointer-events none, so hover and context menu go here.
   */
  'aria-label'?: string;
  role?: React.AriaRole;
  tabIndex?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /**
   * When `name` is a string, controls bold/opacity styling (selected look).
   * Ignored when `name` is a ReactNode.
   */
  isSelected?: boolean;
  /**
   * Baseline bar label: italic + regular weight. Proposal names keep bold.
   */
  nameStyle?: 'proposal' | 'baseline';
  /** Dim the complete bar and its label while baseline is selected. */
  dimmed?: boolean;
};

/**
 * Shared presentation for proposal and project overview bars.
 *
 * Renders the {@link VerticalBar} (CO2 + cost columns + diff card) and
 * a name area beneath it that adopts a tab-style background when the
 * bar is `active`. Used by {@link ProposalBar} (v1), {@link ProposalBar},
 * and {@link BaselineBar}.
 */
export const OverviewBarShell: React.FC<OverviewBarShellProps> = ({
  barWidth,
  nameWidth = barWidth * 1.4,
  nameAreaHeight = AppBarStyles.HEIGHT,
  showNameBottomBorder = true,
  showCo2Label = true,
  maxBarHeight,
  maxBarHeightCss,
  color,
  co2eChartData,
  segments,
  co2ePerGFA,
  co2ePerGfaMax,
  backdropCo2ePerGFA,
  backdropColor,
  backdropCostBarHeight,
  costBarHeight,
  costLabel,
  showInnerBorders,
  innerBorderColor,
  outlineOnly,
  showCo2LabelIcon,
  tooltipPlacement = 'right',
  diffValue,
  active = false,
  onClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  cursor = 'pointer',
  name,
  isSelected,
  nameStyle = 'proposal',
  dimmed = false,
  'aria-label': ariaLabel,
  role,
  tabIndex,
  onKeyDown,
}) => {
  // String label uses the simple centered Typography; otherwise render children directly.
  const isStringName = typeof name === 'string';
  const nameAreaRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const nameArea = nameAreaRef.current;
    if (!active || !nameArea) return;

    let animationFrame = 0;

    const alignOutlineToDevicePixels = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const rect = nameArea.getBoundingClientRect();
        const declaredWidth = nameWidth;
        if (!rect.width || !declaredWidth) return;

        const cssToViewportScale = rect.width / declaredWidth;
        const physicalPixelsPerCssPixel =
          cssToViewportScale * window.devicePixelRatio;
        if (!physicalPixelsPerCssPixel) return;

        const notchWidth = 6;
        const leftEdgeInPhysicalPixels =
          (rect.left + notchWidth * cssToViewportScale) *
          window.devicePixelRatio;
        const rightEdgeInPhysicalPixels =
          (rect.right - notchWidth * cssToViewportScale) *
          window.devicePixelRatio;

        const leftWidthAdjustment =
          (Math.round(leftEdgeInPhysicalPixels) - leftEdgeInPhysicalPixels) /
          physicalPixelsPerCssPixel;
        const rightWidthAdjustment =
          -(Math.round(rightEdgeInPhysicalPixels) - rightEdgeInPhysicalPixels) /
          physicalPixelsPerCssPixel;

        nameArea.style.setProperty(
          '--proposal-left-notch-width',
          `${notchWidth + leftWidthAdjustment}px`,
        );
        nameArea.style.setProperty(
          '--proposal-right-notch-width',
          `${notchWidth + rightWidthAdjustment}px`,
        );
      });
    };

    alignOutlineToDevicePixels();
    window.addEventListener('resize', alignOutlineToDevicePixels);
    window.addEventListener('scroll', alignOutlineToDevicePixels, true);

    const overview = nameArea.closest('[data-proposals-overview]');
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(alignOutlineToDevicePixels);
    resizeObserver?.observe(nameArea);
    if (overview) resizeObserver?.observe(overview);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', alignOutlineToDevicePixels);
      window.removeEventListener('scroll', alignOutlineToDevicePixels, true);
      resizeObserver?.disconnect();
    };
  }, [active, nameWidth]);

  return (
    <Box
      data-proposal-bar=""
      role={role ?? (ariaLabel ? 'group' : undefined)}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor,
        zIndex: 2,
        position: 'relative',
      }}
    >
      <VerticalBar
        barWidth={barWidth}
        maxBarHeight={maxBarHeight}
        maxBarHeightCss={maxBarHeightCss}
        color={color}
        co2eChartData={co2eChartData}
        segments={segments}
        co2ePerGFA={co2ePerGFA}
        co2ePerGfaMax={co2ePerGfaMax}
        backdropCo2ePerGFA={backdropCo2ePerGFA}
        backdropColor={backdropColor}
        backdropCostBarHeight={backdropCostBarHeight}
        costBarHeight={costBarHeight}
        costLabel={costLabel}
        showInnerBorders={showInnerBorders}
        innerBorderColor={innerBorderColor}
        outlineOnly={outlineOnly}
        showCo2Label={showCo2Label}
        showCo2LabelIcon={showCo2LabelIcon}
        tooltipPlacement={tooltipPlacement}
        diffValue={diffValue}
        dimmed={dimmed}
      />

      {/* Name area (tab-style background extends across dividers when active). */}
      <Box
        ref={nameAreaRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          width: nameWidth,
          minHeight: 24,
          px: '16px',
          backgroundColor: active ? 'warmSurface.main' : 'warmSurface.dark',
          opacity: dimmed ? 0.5 : 1,
          transition: 'opacity 150ms ease-in-out',

          boxShadow:
            active || !showNameBottomBorder
              ? 'inset 0 1px 0 rgba(0,0,0,0.1)'
              : 'inset 0 1px 0 rgba(0,0,0,0.1), inset 0 -1px 0 rgba(0,0,0,0.1)',

          // Side notches: opaque bg over neighbour dividers, with rounded
          // outer corners for the active "tab" look.
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 6,
            backgroundColor: active ? 'warmSurface.dark' : 'transparent',
            zIndex: 1,
          },

          '&::before': {
            left: 0,
            ...(active && {
              width: 'var(--proposal-left-notch-width, 6px)',
              borderRight: '1px solid rgba(0,0,0,0.1)',
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              borderBottomRightRadius: 4,
            }),
          },

          '&::after': {
            right: 0,
            ...(active && {
              width: 'var(--proposal-right-notch-width, 6px)',
              borderLeft: '1px solid rgba(0,0,0,0.1)',
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              borderBottomLeftRadius: 4,
            }),
          },
        }}
      >
        {isStringName ? (
          <Typography
            noWrap
            sx={{
              fontSize: '13px',
              ...(nameStyle === 'baseline'
                ? {
                    fontStyle: 'italic',
                    fontWeight: 500,
                    overflow: 'visible',
                    textOverflow: 'clip',
                  }
                : { fontWeight: 700 }),
              opacity: isSelected ? 1 : 0.6,
              textAlign: 'center',
              lineHeight: `${AppBarStyles.HEIGHT}px`,
            }}
          >
            {name}
          </Typography>
        ) : (
          <Box
            sx={{
              height: nameAreaHeight,
              width: '100%',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {name}
          </Box>
        )}
      </Box>
    </Box>
  );
};
